import type { Vehicle } from "@/types";
import { resolveVehicleColor } from "./colors";
import type { VehicleModelDef } from "./models";
import { resolvePhotoUrl } from "./photo-registry";
import { resolveVehicleModelFor } from "./resolve";

// Cache module-level: mesma combinação modelo+cor nunca é re-serializada.
// Compartilhado por todos os mapas (desktop, PWA mobile, mapa expandido) —
// com milhares de veículos, a maioria repete um punhado de combinações.
const dataUriCache = new Map<string, string>();

// Só existe pro caso patológico: cor sem PNG gerado NEM função SVG (modelo
// 100% fotorrealista + hex fora da paleta conhecida). Não deveria acontecer
// em uso normal — resolveVehicleColor só devolve hex de STATUS_TINTS/
// VEHICLE_COLOR_MAP, e o gerador (scripts/render-vehicle-icons) cobre os
// dois + AUTO_PREVIEW_COLOR. Existe só pra nunca quebrar o mapa.
//
// Bug corrigido: o PNG antigo aqui (grayscale+alpha, gray=0x00 alpha=0xff)
// era na verdade um pixel PRETO 100% OPACO, nunca transparente de verdade —
// ficou dormant por muito tempo porque este caminho nunca era exercitado em
// uso normal (toda cor que resolveVehicleColor devolve tem PNG/SVG real).
// Verificado byte a byte (RGBA, r=g=b=a=0) antes de trocar — qualquer cor
// que caia aqui agora fica genuinamente invisível, não um quadrado preto.
const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGNgYGBgAAAABQABpfZFQAAAAABJRU5ErkJggg==";

export function vehicleIconDataUri(model: VehicleModelDef, color: string): string {
  const key = `${model.key}:${color}`;
  const cached = dataUriCache.get(key);
  if (cached) return cached;

  const photoUrl = resolvePhotoUrl(model.key, color);
  const uri = photoUrl
    ? photoUrl
    : model.render
      ? `data:image/svg+xml,${encodeURIComponent(model.render(color))}`
      : TRANSPARENT_PIXEL;

  dataUriCache.set(key, uri);
  return uri;
}

export interface ResolvedVehicleIcon {
  model: VehicleModelDef;
  color: string;
  url: string;
}

// Ponto único usado pelos 3 mapas para saber "qual arte + qual cor" desenhar
// para um veículo — resolve modelo (catálogo), cor (fixa ou automática por
// status) e devolve a URL já cacheada (PNG fotorrealista ou data-URI SVG).
export function resolveVehicleIcon(v: Vehicle): ResolvedVehicleIcon {
  const model = resolveVehicleModelFor(v);
  const color = resolveVehicleColor(v);
  return { model, color, url: vehicleIconDataUri(model, color) };
}
